import React, { useState, Children } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User as UserIcon, Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { claimGuestOrders } from '../../../utils/firebase';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Try signing in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password. Please try again.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email address.';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Please wait a moment and try again.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
// ─── Shared Layout ──────────────────────────────────────────────
function AuthShell({
  eyebrow,
  title,
  subtitle,
  children
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-2147" data-mp="2fw3v7nrk43w3">
      {/* HERO */}
      <section className="bg-bodybg pt-40 pb-12 px-6 border-b border-darkgray" data-id="element-2148" data-mp="2axu0fe9qv5b0">
        <div className="max-w-2xl mx-auto text-center" data-id="element-2149" data-mp="26xa3aap681ov">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} className="flex flex-col items-center" data-id="element-2150" data-mp="26ythzwv6i4oj">
            <motion.p variants={fadeUp} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-2151" data-mp="1ymjgq4nmrpyk">
              {eyebrow}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-2152" data-mp="1tp02h4qxnd6m">
              {title}
            </motion.h1>
            <motion.div variants={fadeUp} className="w-16 h-0.5 bg-tealslate mb-6" data-id="element-2153" data-mp="2mdozarkygwck" />
            <motion.p variants={fadeUp} className="text-base text-[#D8D8D0] max-w-lg leading-relaxed" data-id="element-2154" data-mp="28d4e5orkiofw">
              {subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-[#F9F9F9] py-16 md:py-24 px-6" data-id="element-2155" data-mp="11y3r68kitglg">
        <div className="max-w-md mx-auto" data-id="element-2156" data-mp="11zeu66656kbi">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} data-id="element-2157" data-mp="tgtdxh9ihxt1">
            {children}
          </motion.div>
        </div>
      </section>
    </div>;
}
function FieldLabel({
  htmlFor,
  children
}: {
  htmlFor: string;
  children: React.ReactNode;
}) {
  return <label htmlFor={htmlFor} className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2158" data-mp="arn1gbzba2fo">
      {children}
    </label>;
}
function ErrorBanner({
  message
}: {
  message: string;
}) {
  return <div className="bg-burgundy/10 border-l-4 border-burgundy p-4 flex gap-3 items-start" data-id="element-2159" data-mp="khpvmfi65mfl">
      <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2160" data-mp="fodfyuvgwpyz" />
      <p className="text-sm text-burgundy" data-id="element-2161" data-mp="3o4geaa0ox2an">{message}</p>
    </div>;
}
function SubmitButton({
  loading,
  loadingLabel,
  children
}: {
  loading: boolean;
  loadingLabel: string;
  children: React.ReactNode;
}) {
  return <button type="submit" disabled={loading} className="w-full px-10 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3" data-id="element-2162" data-mp="2mguvpzsmogon">
      {loading ? <>
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2163" data-mp="355i0eh6w741p" />
          {loadingLabel}
        </> : <>
          {children} <ArrowRight className="w-4 h-4" data-id="element-2164" data-mp="3a2ro9semv7nf" />
        </>}
    </button>;
}
// ─── Login ──────────────────────────────────────────────────────
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signIn
  } = useAuth();
  const redirectTo = (location.state as {
    from?: string;
  } | null)?.from ?? '/shop';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(redirectTo, {
        replace: true
      });
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <div className="w-full min-h-screen flex flex-col items-center justify-center pt-32 pb-12 px-6 bg-[#FFFFFF]" data-id="element-2165" data-mp="1v8t9w95ghbiw">
      <div className="w-full max-w-[400px]" data-id="element-2166" data-mp="1r43fkoqg4503">
        <div className="text-center mb-8" data-id="element-2167" data-mp="1m73i327nwakl">
          <h1 className="text-4xl font-bold text-[#111111] mb-2 tracking-tight" data-id="element-2168" data-mp="1m6bo1vtpqkwf">
            Welcome!
          </h1>
          <p className="text-sm font-medium text-[#333333]" data-id="element-2169" data-mp="fooizm25ff5i">
            Access your account and manage your orders
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2170" data-mp="c8c005pfcguy">
          <div data-id="element-2171" data-mp="c6r68iaxp1y5">
            <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2172" data-mp="h7wua2tng11c">
              Email
            </label>
            <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" placeholder="Your email" data-id="element-2173" data-mp="373zri3cl5y8" />
          </div>

          <div data-id="element-2174" data-mp="3ugjhvc6x8wxn">
            <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2175" data-mp="3fpkd6c97isjc">
              Password
            </label>
            <div className="relative" data-id="element-2176" data-mp="3knvkaf84aoxv">
              <input id="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] pl-4 pr-12 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" placeholder="••••••••" data-id="element-2177" data-mp="3kmkjmo9yxxaz" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle-button absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-[#5A5A5A] transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-2178" data-mp="2n4dw6ufn7z3h">
                {showPassword ? <EyeOff className="w-5 h-5" data-id="element-2179" data-mp="3azqa8nro45ol" /> : <Eye className="w-5 h-5" data-id="element-2180" data-mp="3ayonvn57bwrk" />}
              </button>
            </div>
          </div>

          {error && <ErrorBanner message={error} data-id="element-2181" data-mp="3kquqrrwzrylj" />}

          <button type="submit" disabled={loading} className="w-full px-6 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md flex items-center justify-center gap-2 mt-2 shadow-sm text-[18px]" data-id="element-2182" data-mp="3kpt40q6m5nnz">
            {loading ? <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2183" data-mp="3otr16vcdooee" />
                Signing in…
              </> : 'Sign In'}
          </button>

          <div className="pt-4 flex flex-col items-center gap-3 text-sm" data-id="element-2184" data-mp="14xj4ouc6riuz">
            <Link to="/forgot-password" className="text-[#5A5A5A] hover:text-[#111111] transition-colors" data-id="element-2185" data-mp="190fim7qs6qsl">
              Forgot password?
            </Link>

            <p className="text-[#5A5A5A]" data-id="element-2186" data-mp="1dyh9vfyd1wf5">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#111111] font-bold hover:text-tealslate transition-colors underline underline-offset-2" data-id="element-2187" data-mp="hakqk3bjgn08">
                Create one
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>;
}
// ─── Sign Up ────────────────────────────────────────────────────
export function Signup() {
  const navigate = useNavigate();
  const {
    signUp
  } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, name.trim(), marketingOptIn);
      try {
        await claimGuestOrders();
      } catch (err) {
        console.error('Failed to claim guest orders:', err);
      }
      navigate('/account', {
        replace: true
      });
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <div className="w-full min-h-screen flex flex-col items-center justify-center pt-32 pb-12 px-6 bg-[#FFFFFF]" data-id="element-2188" data-mp="2tk1begsekitb">
      <div className="w-full max-w-[400px]" data-id="element-2189" data-mp="2pifsadp9z8ao">
        <div className="text-center mb-8" data-id="element-2190" data-mp="2kil3cfp28ss9">
          <h1 className="text-4xl font-bold text-[#111111] mb-2 tracking-tight" data-id="element-2191" data-mp="2kjmoxe3pcxo8">
            Create Account
          </h1>
          <p className="text-sm font-medium text-[#333333]" data-id="element-2192" data-mp="2qntxnr0b858l">
            Access your account and manage your orders
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2193" data-mp="35b59zv4bft9r">
          <div data-id="element-2194" data-mp="30dn5qi18u283">
            <label htmlFor="name" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2195" data-mp="30d3qj92z9rxr">
              Full Name
            </label>
            <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" placeholder="Jane Smith" data-id="element-2196" data-mp="2co1bm8zqad6y" />
          </div>

          <div data-id="element-2197" data-mp="1vhnh33q8gdy">
            <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2198" data-mp="1v85a1oxbdit">
              Email
            </label>
            <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" placeholder="Your email" data-id="element-2199" data-mp="3t1j37oymzy5x" />
          </div>

          <div data-id="element-2200" data-mp="3vmh358krfoda">
            <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2201" data-mp="3vlxj9mbjupwm">
              Password
            </label>
            <div className="relative" data-id="element-2202" data-mp="n774ybjzr3as">
              <input id="password" type={showPassword ? 'text' : 'password'} required minLength={6} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] pl-4 pr-12 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" placeholder="At least 6 characters" data-id="element-2203" data-mp="s5qs2fabml4q" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle-button absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-[#5A5A5A] transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-2204" data-mp="163ovwqr0mz5z">
                {showPassword ? <EyeOff className="w-5 h-5" data-id="element-2205" data-mp="1brjwuwodmbw6" /> : <Eye className="w-5 h-5" data-id="element-2206" data-mp="1bqh8avej0ljf" />}
              </button>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer group pt-2" data-id="element-2207" data-mp="16re1p6iwe79c">
            <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-2208" data-mp="11tw246qrt4e0" />
            <span className="text-xs text-[#5A5A5A] leading-relaxed" data-id="element-2209" data-mp="t1kc2lzkjiyk">
              Send me occasional updates on new books, supplements, and articles.
            </span>
          </label>

          {error && <ErrorBanner message={error} data-id="element-2210" data-mp="j2d125gb40em" />}

          <button type="submit" disabled={loading} className="w-full px-6 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md flex items-center justify-center gap-2 mt-2 shadow-sm text-[18px]" data-id="element-2211" data-mp="e3rxbw1uiylf">
            {loading ? <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2212" data-mp="dyalu923mqyt" />
                Creating account…
              </> : 'Create Account'}
          </button>

          <div className="pt-4 flex flex-col items-center gap-3 text-sm" data-id="element-2213" data-mp="3heztxzhby8ql">
            <p className="text-[#5A5A5A]" data-id="element-2214" data-mp="3hcws8i6ter61">
              Already have an account?{' '}
              <Link to="/login" className="text-[#111111] font-bold hover:text-tealslate transition-colors underline underline-offset-2" data-id="element-2215" data-mp="3me43wsc2wahz">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>;
}
// ─── Forgot Password ────────────────────────────────────────────
export function ForgotPassword() {
  const {
    resetPassword
  } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthShell eyebrow="Reset Password" title="Forgot your password?" subtitle="Enter your email and we'll send you a link to reset it." data-id="element-2216" data-mp="1rwb3ir225crx">
      {sent ? <div className="bg-white border-l-4 border-tealslate p-8 flex gap-5" data-id="element-2217" data-mp="21uqsandm49y9">
          <CheckCircle2 className="w-8 h-8 text-tealslate shrink-0" data-id="element-2218" data-mp="1ww74sijdeq3s" />
          <div data-id="element-2219" data-mp="1wuw10ipxdico">
            <h3 className="text-2xl font-serif text-[#1F1F1F] mb-2" data-id="element-2220" data-mp="7hr2y2jfk1ds">
              Check your email.
            </h3>
            <p className="text-[#3B3B3B] leading-relaxed mb-4" data-id="element-2221" data-mp="2j7m2gjp1fsa">
              If an account exists for <strong data-id="element-2222" data-mp="2e6ay39hnhcb4">{email}</strong>, we've sent a
              password reset link. Check your spam folder if you don't see it.
            </p>
            <Link to="/login" className="text-xs font-bold text-tealslate uppercase tracking-wider hover:text-navyslate transition-colors inline-flex items-center gap-2" data-id="element-2223" data-mp="2j4kzw2xvrb5p">
              Back to sign in <ArrowRight className="w-3 h-3" data-id="element-2224" data-mp="29cyejdrtvvp8" />
            </Link>
          </div>
        </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2225" data-mp="20emphg6m15yw">
          <div data-id="element-2226" data-mp="20g7kt80r0sxt">
            <FieldLabel htmlFor="email" data-id="element-2227" data-mp="20cjs2b34oak5">Email</FieldLabel>
            <div className="relative" data-id="element-2228" data-mp="1lif8spueumod">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2229" data-mp="1ljgl0xn5xf7o" />
              <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-2230" data-mp="1qijdzjpvbuza" />
            </div>
          </div>

          {error && <ErrorBanner message={error} data-id="element-2231" data-mp="39fen1s1v1do4" />}

          <SubmitButton loading={loading} loadingLabel="Sending…" data-id="element-2232" data-mp="35dt3xoyqg35h">
            Send Reset Link
          </SubmitButton>

          <p className="text-sm text-[#3B3B3B] text-center pt-4" data-id="element-2233" data-mp="5yo123pvuq0s">
            Remembered it?{' '}
            <Link to="/login" className="font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-2234" data-mp="z1ntua219rz">
              Back to sign in
            </Link>
          </p>
        </form>}
    </AuthShell>;
}